import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { loadData, saveData } from '../utils/storage';
import { generateId } from '../utils/idUtils';
import { toDateString } from '../utils/dateUtils';
import { HOLIDAYS_PROJECT_ID } from '../utils/constants';
import { SAVE_DEBOUNCE_MS } from '../constants/layout';
import { DEFAULT_MILESTONE_ICON } from '../constants/types';
import { useTranslation } from '../i18n';

/**
 * LocalStorage-based data source for calendar data
 * @param {string} calendarId - Calendar identifier for storage key
 * @returns {Object} Calendar data and CRUD operations
 */
export function useLocalDataSource(calendarId = 'default') {
  const { t } = useTranslation();
  const personalProjectName = t('project.defaultName');
  const storageKey = calendarId === 'default' ? 'lincal_data' : `lincal-data-${calendarId}`;
  
  const [data, setData] = useState(() => loadData(storageKey, personalProjectName));
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const saveTimeoutRef = useRef(null);
  
  // Selected project ID - persisted in data, defaults to first project
  const selectedProjectId = data.selectedProjectId || data.projects[0]?.id || null;
  
  const clearError = useCallback(() => setError(null), []);

  // Auto-save to LocalStorage with debouncing
  useEffect(() => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(() => {
      try {
        saveData(data, storageKey);
      } catch (error) {
        // Log the error - UI layer should handle showing errors to user
        // This is a background save, so we don't want to interrupt the user
        console.error('Failed to auto-save calendar data:', error);
      }
    }, SAVE_DEBOUNCE_MS);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [data, storageKey]);

  // Projects CRUD
  const addProject = useCallback(async (name, color) => {
    setIsLoading(true);
    setError(null);
    try {
      const newProject = {
        id: generateId('proj'),
        name,
        color,
        visible: true,
        createdAt: new Date().toISOString()
      };

      setData(prev => ({
        ...prev,
        projects: [...prev.projects, newProject]
      }));

      return newProject;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const updateProject = useCallback(async (id, updates) => {
    setIsLoading(true);
    setError(null);
    try {
      setData(prev => ({
        ...prev,
        projects: prev.projects.map(proj =>
          proj.id === id ? { ...proj, ...updates } : proj
        )
      }));
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const deleteProject = useCallback(async (id) => {
    setIsLoading(true);
    setError(null);
    try {
      setData(prev => {
        const newProjects = prev.projects.filter(proj => proj.id !== id);
        
        // If the deleted project was selected, select the first remaining project
        let newSelectedId = prev.selectedProjectId;
        if (prev.selectedProjectId === id) {
          newSelectedId = newProjects[0]?.id || null;
        }
        
        return {
          ...prev,
          projects: newProjects,
          selectedProjectId: newSelectedId,
          // Remove projectId from events and milestones
          events: prev.events.map(evt =>
            evt.projectId === id ? { ...evt, projectId: null } : evt
          ),
          milestones: prev.milestones.map(ms =>
            ms.projectId === id ? { ...ms, projectId: null } : ms
          )
        };
      });
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const toggleProjectVisibility = useCallback(async (id) => {
    setData(prev => ({
      ...prev,
      projects: prev.projects.map(proj =>
        proj.id === id ? { ...proj, visible: !proj.visible } : proj
      )
    }));
  }, []);

  const reorderProjects = useCallback(async (orderedIds) => {
    setData(prev => {
      // Create a map of current projects by ID
      const projectMap = new Map(prev.projects.map(p => [p.id, p]));
      
      // Build new array in the specified order
      const reordered = orderedIds
        .map(id => projectMap.get(id))
        .filter(Boolean); // Remove any undefined entries
      
      return {
        ...prev,
        projects: reordered
      };
    });
  }, []);

  const selectProject = useCallback(async (id) => {
    setData(prev => ({
      ...prev,
      selectedProjectId: id
    }));
  }, []);

  // Events CRUD
  const addEvent = useCallback(async (title, startDate, endDate, projectId = null, description = '') => {
    setIsLoading(true);
    setError(null);
    try {
      const newEvent = {
        id: generateId('evt'),
        title,
        description,
        startDate: toDateString(startDate),
        endDate: toDateString(endDate),
        projectId,
        lockStart: false,
        lockEnd: false,
        lockDuration: false,
        createdAt: new Date().toISOString()
      };

      setData(prev => ({
        ...prev,
        events: [...prev.events, newEvent]
      }));

      return newEvent;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const updateEvent = useCallback(async (id, updates) => {
    setIsLoading(true);
    setError(null);
    try {
      setData(prev => ({
        ...prev,
        events: prev.events.map(evt =>
          evt.id === id ? { ...evt, ...updates } : evt
        )
      }));
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const deleteEvent = useCallback(async (id) => {
    setIsLoading(true);
    setError(null);
    try {
      setData(prev => ({
        ...prev,
        events: prev.events.filter(evt => evt.id !== id)
      }));
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Milestones CRUD
  const addMilestone = useCallback(async (title, date, icon = DEFAULT_MILESTONE_ICON, projectId = null, color = null, description = '') => {
    setIsLoading(true);
    setError(null);
    try {
      const newMilestone = {
        id: generateId('ms'),
        title,
        description,
        date: toDateString(date),
        icon,
        projectId,
        color,
        locked: false,
        createdAt: new Date().toISOString()
      };

      setData(prev => ({
        ...prev,
        milestones: [...prev.milestones, newMilestone]
      }));

      return newMilestone;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const updateMilestone = useCallback(async (id, updates) => {
    setIsLoading(true);
    setError(null);
    try {
      setData(prev => ({
        ...prev,
        milestones: prev.milestones.map(ms =>
          ms.id === id ? { ...ms, ...updates } : ms
        )
      }));
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const deleteMilestone = useCallback(async (id) => {
    setIsLoading(true);
    setError(null);
    try {
      setData(prev => ({
        ...prev,
        milestones: prev.milestones.filter(ms => ms.id !== id)
      }));
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Import/Export
  const importData = useCallback(async (newData) => {
    setIsLoading(true);
    setError(null);
    try {
      setData(newData);
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const resetData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const defaultData = {
        version: 1,
        projects: [{
          id: generateId('proj'),
          name: 'Personal',
          color: '#4285f4',
          visible: true,
          createdAt: new Date().toISOString()
        }],
        events: [],
        milestones: []
      };
      setData(defaultData);
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Holidays helpers
  const addHoliday = useCallback(async (title, startDate, endDate, description = '') => {
    return await addEvent(title, startDate, endDate, HOLIDAYS_PROJECT_ID, null, description);
  }, [addEvent]);

  const updateHoliday = useCallback(async (id, updates) => {
    // Ensure projectId stays as holidays project
    await updateEvent(id, { ...updates, projectId: HOLIDAYS_PROJECT_ID });
  }, [updateEvent]);

  const deleteHoliday = useCallback(async (id) => {
    await deleteEvent(id);
  }, [deleteEvent]);

  // Filter holidays from regular projects list (for UI)
  const regularProjects = data.projects.filter(p => !p.isSystem);

  // Get holidays (events that belong to holidays project)
  const holidays = data.events.filter(e => e.projectId === HOLIDAYS_PROJECT_ID);

  return useMemo(() => ({
    // Data
    projects: regularProjects,
    events: data.events,
    milestones: data.milestones,
    holidays,
    selectedProjectId,
    
    // Project operations
    addProject,
    updateProject,
    deleteProject,
    toggleProjectVisibility,
    reorderProjects,
    selectProject,
    
    // Event operations
    addEvent,
    updateEvent,
    deleteEvent,
    
    // Milestone operations
    addMilestone,
    updateMilestone,
    deleteMilestone,
    
    // Holiday operations
    addHoliday,
    updateHoliday,
    deleteHoliday,
    
    // Import/Export
    importData,
    resetData,
    
    // Raw data for export
    data,
    
    // Loading and error state
    isLoading,
    error,
    clearError,
  }), [
    regularProjects,
    data,
    holidays,
    selectedProjectId,
    addProject,
    updateProject,
    deleteProject,
    toggleProjectVisibility,
    reorderProjects,
    selectProject,
    addEvent,
    updateEvent,
    deleteEvent,
    addMilestone,
    updateMilestone,
    deleteMilestone,
    addHoliday,
    updateHoliday,
    deleteHoliday,
    importData,
    resetData,
    isLoading,
    error,
    clearError,
  ]);
}
